/**
 * Format dispatch: read any panel format from bytes.
 *
 * Format resolution order: an explicit `format`, then the filename
 * extension, then content sniffing ($FL2 => SPSS, the ZIP signature
 * => Excel, the ReadStat signature detector for .dta/.sas7bdat/.xpt,
 * otherwise delimited text read as CSV).
 */
import { readDelimitedTable } from './delimited.js';
import { IoError, unsupportedFormat } from './errors.js';
import { readSpreadsheetTable } from './spreadsheet.js';
import { readStatTable } from './statfmt.js';
const EXTENSION_FORMATS = {
    sav: 'sav',
    dta: 'dta',
    sas7bdat: 'sas7bdat',
    xpt: 'xpt',
    csv: 'csv',
    tsv: 'tsv',
    txt: 'csv',
    xlsx: 'xlsx',
};
function sniffFormat(bytes) {
    const head = bytes.subarray(0, 4);
    const text = new TextDecoder('latin1').decode(head);
    if (text === '$FL2')
        return 'sav';
    if (text === '$FL3') {
        throw new IoError({
            code: 'IO_UNSUPPORTED_FORMAT',
            severity: 'error',
            message: 'This is a compressed SPSS file (.zsav), which is not in the supported format list. Save it as a standard .sav file and try again.',
            detail: 'zsav ($FL3) stream',
        });
    }
    if (bytes[0] === 0x50 && bytes[1] === 0x4b)
        return 'xlsx'; // 'PK'
    if (new TextDecoder('latin1')
        .decode(bytes.subarray(0, 30))
        .includes('HEADER RECORD')) {
        return 'xpt';
    }
    return 'csv';
}
export async function readTable(input) {
    const { bytes } = input;
    if (bytes.length === 0) {
        throw new IoError({
            code: 'IO_EMPTY_FILE',
            severity: 'error',
            message: 'This file is empty. Choose a file with data in it.',
        });
    }
    let format = input.format;
    if (!format && input.filename) {
        const ext = input.filename.split('.').pop()?.toLowerCase() ?? '';
        if (ext === 'zsav') {
            throw new IoError({
                code: 'IO_UNSUPPORTED_FORMAT',
                severity: 'error',
                message: 'Compressed SPSS files (.zsav) are not in the supported format list. Save the file as a standard .sav and try again.',
                detail: 'extension .zsav',
            });
        }
        format = EXTENSION_FORMATS[ext];
    }
    if (!format) {
        // Sniffing covers sav/xlsx/xpt; use the codec's signature
        // detector for the binary statistical formats before falling
        // back to delimited text.
        try {
            format = sniffFormat(bytes);
        }
        catch (e) {
            if (e instanceof IoError)
                throw e;
            format = 'csv';
        }
        if (format === 'csv') {
            try {
                const codec = await import('@irbisadm/statfmt');
                const detected = codec.detectFormat(bytes);
                if (detected === 'xport') {
                    format = 'xpt';
                }
                else if (detected === 'dta' ||
                    detected === 'sas7bdat' ||
                    detected === 'sav') {
                    format = detected;
                }
            }
            catch {
                // Detection is best-effort; CSV remains the fallback.
            }
        }
    }
    switch (format) {
        case 'sav':
        case 'dta':
        case 'sas7bdat':
        case 'xpt':
            return readStatTable(bytes, format);
        case 'xlsx':
            return readSpreadsheetTable(bytes, { sheet: input.sheet });
        case 'csv':
        case 'tsv':
            return readDelimitedTable(bytes, {
                format,
                delimiter: input.delimiter,
            });
        default:
            throw unsupportedFormat(`unresolved format for ${input.filename ?? 'input'}`);
    }
}
//# sourceMappingURL=read.js.map